import type { Place } from "../../types/warehouse.types";
import { getRowId } from "../../utils/placeUtils";
import { ObjectPool } from "../core/ObjectPool";
import { NumericKeyMap } from "../core/NumericKeyMap";
import type { PlaceColumn } from "../core/types";

/**
 * Builds columns from places using object pooling
 *
 * Pattern: Builder Pattern + Object Pool
 */
export class ColumnBuilder {
  private columnPool: ObjectPool<PlaceColumn>;
  private columnMap = new NumericKeyMap<PlaceColumn>();

  constructor() {
    this.columnPool = new ObjectPool<PlaceColumn>(
      () => ({ places: [], baseX: 0, baseY: 0, maxLevel: 0, depth: 0 }),
      (col) => {
        col.places.length = 0;
        col.maxLevel = 0;
        col.depth = 0;
      },
      100 // Initial pool size
    );
  }

  /**
   * Build columns from places
   */
  build(
    places: Map<string, Place>,
    activeLevels: Set<number>,
    activeRows: Set<string>
  ): { columns: PlaceColumn[]; globalMaxLevel: number } {
    // Release all columns back to pool
    this.columnPool.releaseAll();
    this.columnMap.clear();

    const columns: PlaceColumn[] = [];
    let globalMaxLevel = 1;

    places.forEach((place) => {
      const rowId = getRowId(place);
      if (!activeLevels.has(place.level) || !activeRows.has(rowId)) {
        return;
      }

      globalMaxLevel = Math.max(globalMaxLevel, place.level);

      let column = this.columnMap.get(place.baseX, place.baseY);

      if (!column) {
        column = this.columnPool.acquire();
        column.baseX = place.baseX;
        column.baseY = place.baseY;

        this.columnMap.set(place.baseX, place.baseY, column);
        columns.push(column);
      }

      column.places[place.level] = place;
      column.maxLevel = Math.max(column.maxLevel, place.level);
    });

    return { columns, globalMaxLevel };
  }

  /**
   * Get pool statistics
   */
  getPoolStats() {
    return this.columnPool.getStats();
  }
}
